/**
 * siteNav.js — single source of truth for global findability nav.
 * Drives the crawlable global footer AND the /sitemap page, so both stay
 * consistent and every link points at a REAL, existing route (no dead ends).
 * (Site-wide Findability command §1 footer, §9 sitemap.)
 */
import { footerColumns } from "@/lib/navRegistry";
// Jun 2026 · derived from the ONE canonical registry (lib/navRegistry.js). Six spec groups:
// Learn · Play · Explore · My Quest · Grown-ups · About. Sitemap keeps the full list (incl. footer:false items).
export const FOOTER_COLUMNS = footerColumns().map((c) => (c.title === "Grown-ups" ? { ...c, grownup: true } : c));

// Routes where the global footer is suppressed — full-screen immersive
// activities (findability belongs BETWEEN activities, not during them).
const ACTIVITY_PREFIXES = [
  "/live/room/", "/live/host", "/live/solo",
  "/learn/ai/pattern-trainer", "/learn/ai/train-the-robot",
  "/code-breaker/play/", "/draw-along/",
];
const ACTIVITY_EXACT = new Set([
  "/coloring", "/pixel-art", "/mandala", "/kaleidoscope", "/stained-glass",
  "/dodge", "/dodge-classic", "/squishy", "/squishy-classic", "/snap",
]);
export function isActivityRoute(path) {
  if (!path) return false;
  if (ACTIVITY_EXACT.has(path)) return true;
  return ACTIVITY_PREFIXES.some((p) => path.startsWith(p));
}

// Active-learning surfaces (lessons, tests, practice, quests, games, live play).
// Layout swaps the full header/footer/tab bar for a slim Back bar so the child
// stays in the activity. Hubs (/games, /academy, /live, /learn-topics) stay website-like.
const ACTIVE_LEARNING_PREFIXES = [
  "/start/", "/lesson/", "/practice/", "/learn-topics/", "/units/", "/diagnostic/", "/games/", "/live/",
  "/story/", "/learn/ai/", "/code-breaker/play/", "/draw-along/", "/academy/maths/library/",
];
const ACTIVE_LEARNING_EXACT = new Set([
  "/quiz", "/times-tables-quest", "/times-tables-quest/check", "/english-quest", "/maths-academy", "/first-quest", "/focus",
  "/spell", "/math", "/spell-countries", "/math-snap", ...ACTIVITY_EXACT,
]);
const ACADEMY_ACTIVITY = /^\/academy\/[^/]+\/[^/]+\/(placement|[^/]+\/[^/]+)$/; // skill pages, unit tests, placement
export function isActiveLearningRoute(path) {
  if (!path) return false;
  const p = path.replace(/\/+$/, "") || "/";
  if (ACTIVE_LEARNING_EXACT.has(p) || isActivityRoute(p) || ACADEMY_ACTIVITY.test(p)) return true;
  return ACTIVE_LEARNING_PREFIXES.some((x) => p.startsWith(x));
}
// Where Back should land when there is no in-app history (hard refresh / deep link).
export function activityHubFor(path) {
  const p = path || "/";
  if (p.startsWith("/games/") || p === "/quiz" || p === "/spell" || p === "/spell-countries" || p === "/math-snap") return "/games";
  if (p.startsWith("/live/")) return "/live";
  if (p.startsWith("/academy/")) return "/academy";
  if (p.startsWith("/story/")) return "/stories";
  if (p.startsWith("/learn/ai/")) return "/learn/ai";
  if (p.startsWith("/units/") || p.startsWith("/diagnostic/")) return "/learn-pathways";
  if (p.startsWith("/lesson/") || p.startsWith("/practice/") || p.startsWith("/learn-topics/")) return "/learn-topics";
  return "/";
}
